import { Badge } from '@buyerflo/ui/components/Badge'
import { Card } from '@buyerflo/ui/components/Card'
import { FormSection } from '@buyerflo/ui/components/FormSection'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Button from 'components/button'
import CopyButton from 'components/CopyButton'
import Loading from 'components/Loading'
import { affiliateStatusTone, fmtAud, fmtDate, referralStatusTone } from 'affiliate/shared'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { SizableText, XStack, YStack } from 'tamagui'
import { useShowSnack } from 'components/snackbar'
import { useTRPC } from 'utils/trpc'
import EditAffiliateModal from './EditAffiliateModal'

const StatTile = ({ label, value }: { label: string; value: string }) => (
    <Card padded flex={1} minWidth={150}>
        <SizableText fontSize={11} textTransform="uppercase" letterSpacing={0.4} color="$colorMuted">{label}</SizableText>
        <SizableText fontSize={22} fontWeight="700">{value}</SizableText>
    </Card>
)

export default function AffiliateDetail() {
    const { affiliateId = '' } = useParams()
    const trpc = useTRPC()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const showSnack = useShowSnack()
    const [editing, setEditing] = useState(false)

    // The list page caches per-affiliate earnings/status + org-wide totals; a payout or edit here
    // changes them, so invalidate it to avoid the list showing stale figures after navigating back.
    const invalidateList = () =>
        queryClient.invalidateQueries({ queryKey: trpc.affiliateAdmin.listAffiliates.queryKey() })

    const affiliateQuery = useQuery(trpc.affiliateAdmin.getAffiliate.queryOptions({ affiliateId }))
    const referralsQuery = useQuery(trpc.affiliateAdmin.getAffiliateReferrals.queryOptions({ affiliateId }))
    const ledgerQuery = useQuery(trpc.affiliateAdmin.getAffiliateLedger.queryOptions({ affiliateId }))
    const payoutMutation = useMutation(trpc.affiliateAdmin.triggerPayout.mutationOptions())

    if (affiliateQuery.isLoading) return <Loading />
    if (!affiliateQuery.data) return <SizableText>Affiliate not found.</SizableText>

    const { affiliate, earnings } = affiliateQuery.data
    const referrals = referralsQuery.data ?? []
    const ledger = ledgerQuery.data ?? []

    const runPayout = async () => {
        const res = await payoutMutation.mutateAsync({ affiliateId })
        showSnack(
            res.status === 'paid'
                ? `Paid ${fmtAud(res.amountCents)}`
                : res.status === 'skipped'
                  ? `Skipped: ${res.reason}`
                  : `Error: ${res.error}`,
            res.status === 'error' ? 'danger' : res.status === 'paid' ? 'success' : 'info',
        )
        affiliateQuery.refetch()
        ledgerQuery.refetch()
        invalidateList()
    }

    return (
        <YStack gap="$4">
            <XStack alignItems="center" gap="$3">
                <Button variant="outlined" size="$2" onPress={() => navigate('/admin/affiliates')}>← Back</Button>
                <YStack flex={1} minWidth={0}>
                    <SizableText fontSize="$7" fontWeight="700">{affiliate.name}</SizableText>
                    <SizableText color="$colorMuted">{affiliate.email}</SizableText>
                </YStack>
                <Badge tone={affiliateStatusTone[affiliate.status] ?? 'neutral'}>{affiliate.status}</Badge>
                <Button variant="outlined" onPress={() => setEditing(true)}>Edit</Button>
                <Button loading={payoutMutation.isPending} onPress={runPayout}>Pay out now</Button>
            </XStack>

            <XStack gap="$3" flexWrap="wrap">
                <StatTile label="Paid to date" value={fmtAud(earnings.paidToDateCents)} />
                <StatTile label="Unpaid (owed)" value={fmtAud(earnings.unpaidCents)} />
                <StatTile label="Lifetime" value={fmtAud(earnings.lifetimeCents)} />
                <StatTile label="Active referrals" value={String(referrals.filter(r => r.status === 'active').length)} />
            </XStack>

            <FormSection title="Configuration">
                <XStack gap="$5" flexWrap="wrap">
                    <YStack><SizableText color="$colorMuted" size="$1">Promo code</SizableText><SizableText fontFamily="$mono">{affiliate.promoCode}</SizableText></YStack>
                    <YStack><SizableText color="$colorMuted" size="$1">Discount</SizableText><SizableText>{affiliate.discountPercent}% / {affiliate.discountDurationMonths}mo</SizableText></YStack>
                    <YStack><SizableText color="$colorMuted" size="$1">Commission</SizableText><SizableText>{affiliate.commissionPercent}% / {affiliate.commissionDurationMonths}mo</SizableText></YStack>
                    <YStack><SizableText color="$colorMuted" size="$1">Payouts</SizableText><SizableText color={affiliate.payoutFailed ? '$statusDanger' : undefined}>{affiliate.payoutFailed ? 'Payout failed' : affiliate.connectOnboardingComplete ? 'Connected' : 'Not set up'}</SizableText></YStack>
                </XStack>
                {affiliate.payoutFailed && (
                    <SizableText size="$2" color="$statusDanger">
                        Last bank payout failed{affiliate.payoutFailureReason ? `: ${affiliate.payoutFailureReason}` : ''}. The affiliate needs to update their bank details (funds are safe in their Stripe balance).
                    </SizableText>
                )}
                {affiliate.referralLink && (
                    <XStack gap="$2" alignItems="center">
                        <SizableText flex={1} size="$2" color="$colorSoft">{affiliate.referralLink}</SizableText>
                        <CopyButton text={affiliate.referralLink} size="$2" variant="outlined" />
                    </XStack>
                )}
            </FormSection>

            <FormSection title={`Referrals (${referrals.length})`}>
                {referrals.length === 0 && <SizableText color="$colorMuted">No referrals yet.</SizableText>}
                {referrals.map(r => (
                    <XStack key={r.referralId} gap="$3" alignItems="center" paddingVertical="$1.5">
                        <Badge tone={referralStatusTone[r.status] ?? 'neutral'}>{r.status}</Badge>
                        <SizableText flex={1} size="$2">Org {r.orgId.slice(0, 8)}…</SizableText>
                        <SizableText size="$1" color="$colorMuted">Joined {fmtDate(r.signedUpAt)}</SizableText>
                        <SizableText size="$1" color="$colorMuted">Window ends {fmtDate(r.commissionWindowEnd)}</SizableText>
                    </XStack>
                ))}
            </FormSection>

            <FormSection title="Commission ledger">
                {ledger.length === 0 && <SizableText color="$colorMuted">No ledger entries yet.</SizableText>}
                {[...ledger].sort((a, b) => b.createdAt - a.createdAt).map(e => (
                    <XStack key={e.id} gap="$3" alignItems="center" paddingVertical="$1.5">
                        <Badge tone={e.amountCents >= 0 ? 'ok' : 'danger'}>{e.type}</Badge>
                        <SizableText flex={1} size="$2" color="$colorMuted">{e.note ?? e.stripeInvoiceId ?? ''}</SizableText>
                        <SizableText size="$1" color="$colorMuted">{fmtDate(e.createdAt)}</SizableText>
                        <SizableText fontWeight="600" color={e.amountCents >= 0 ? '$statusOk' : '$statusDanger'}>{fmtAud(e.amountCents)}</SizableText>
                    </XStack>
                ))}
            </FormSection>

            {editing && (
                <EditAffiliateModal
                    affiliate={affiliate}
                    open={editing}
                    onClose={() => setEditing(false)}
                    onSaved={() => {
                        setEditing(false)
                        affiliateQuery.refetch()
                        invalidateList()
                    }}
                />
            )}
        </YStack>
    )
}
